import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  CheckCircle,
  CircleAlert,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { createPortal } from "react-dom";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { getOperationalCapabilities } from "@/features/auth/operationalCapabilities";
import { canCompleteClientOrder, isClientLike } from "@/features/auth/roles";
import {
  useApproveChangeRequest,
  useChangeRequest,
  useCompleteOrder,
  useDeleteChangeRequest,
  useDeleteOrder,
  useEnrichedOrderDetail,
  useOrderSummaries,
  usePrefetchEnrichedOrderDetails,
  useRejectChangeRequest,
  useReopenOrder,
} from "@/features/order/hooks";
import type {
  EnrichedOrder,
  OrderStatus,
  OrderSummary,
  OrderSummaryFilters,
  RemoveOrderItemResponse,
} from "@/features/order/types";
import {
  isFinancialChangeRequest,
  isFinancialEnrichedOrder,
  isOperationalOrderSummaryPage,
} from "@/features/order/types";
import {
  buildOrderCardDisplay,
  getOrderTotalPrice,
  toggleOrderExpansion,
} from "@/features/order/display";
import { getOrderMutationErrorMessage } from "@/features/order/errors";
import { exportOrderPdf } from "@/features/pedidoReport/api";
import {
  formatIsoDateForDisplay,
  maskOrderDateForDisplay,
  parseDisplayDateToIso,
} from "@/features/order/filters";
import { todaySaoPaulo } from "@/lib/dates";
import { useClients } from "@/features/client/hooks";
import { useStores } from "@/features/store/hooks";
import { FloatingToast } from "@/components/feedback/FloatingToast";
import { CloseIconButton } from "@/components/ui/CloseIconButton";
import { InlineLoadError } from "@/components/feedback/InlineLoadError";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import ChangeRequestModal from "@/components/orders/ChangeRequestModal";
import { ChangeRequestCard } from "@/components/orders/ChangeRequestCard";
import { ChangeRequestDiff } from "@/components/orders/ChangeRequestDiff";
import {
  hasOrderCardActions,
  hasOrderCardRowActions,
  OrderCardActions,
} from "@/components/orders/OrderCardActions";
import { OrderEmptyState } from "@/components/orders/OrderEmptyState";
import { OrderCard } from "@/components/orders/OrderCard";
import {
  ORDER_STATUS_FILTER_OPTIONS,
  OrderStatusCheckboxGroup,
} from "@/components/orders/OrderStatusCheckboxGroup";
import { SuperAddItemPanel } from "@/components/orders/SuperAddItemPanel";
import { AdminHelperOperationalOrdersView } from "@/components/orders/AdminHelperOperationalOrdersView";
import { OperationalFilterSurface } from "@/components/operational/OperationalFilterSurface";
import { getNumberedPageTokens } from "@/components/operational/OperationalPager";
import {
  OperationalPageFrame,
  OperationalPageHeader,
  OperationalToolbar,
} from "@/components/operational/OperationalPageLayout";
import { OperationalSearchField } from "@/components/operational/OperationalSearchField";
import { OperationalSelect } from "@/components/operational/OperationalSelect";
import { isLegacyDirectKgItem } from "@/lib/quantity";
import { useDialogFocus } from "@/hooks/useDialogFocus";

const PAGE_SIZE = 10;
const DETAIL_PREFETCH_COUNT = 5;

type ClientOrderFilters = {
  search: string;
  status: OrderStatus | "";
  date_from: string;
  date_to: string;
};

type SuperOrderFilters = ClientOrderFilters & {
  client_id: string;
  store_id: string;
  statuses: OrderStatus[];
};

const DEFAULT_CLIENT_FILTERS: ClientOrderFilters = {
  search: "",
  status: "",
  date_from: "",
  date_to: "",
};

const DEFAULT_SUPER_FILTERS: SuperOrderFilters = {
  ...DEFAULT_CLIENT_FILTERS,
  client_id: "",
  store_id: "",
  statuses: [],
};

function normalizeText(value: string) {
  return value.trim();
}

function toClientQueryFilters(
  filters: ClientOrderFilters,
): OrderSummaryFilters | undefined {
  const normalized = {
    ...(normalizeText(filters.search)
      ? { search: normalizeText(filters.search) }
      : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.date_from ? { date_from: filters.date_from } : {}),
    ...(filters.date_to ? { date_to: filters.date_to } : {}),
  };
  return Object.keys(normalized).length ? normalized : undefined;
}

export function toSuperQueryFilters(
  filters: SuperOrderFilters,
): OrderSummaryFilters | undefined {
  const normalized = {
    ...(toClientQueryFilters(filters) ?? {}),
    ...(filters.client_id ? { client_id: filters.client_id } : {}),
    ...(filters.store_id ? { store_id: filters.store_id } : {}),
    ...(filters.statuses.length ? { statuses: filters.statuses } : {}),
  };
  return Object.keys(normalized).length ? normalized : undefined;
}

function normalizeClientFilters(
  filters: ClientOrderFilters,
): ClientOrderFilters {
  return {
    search: normalizeText(filters.search),
    status: filters.status,
    date_from: filters.date_from,
    date_to: filters.date_to,
  };
}

function normalizeSuperFilters(filters: SuperOrderFilters): SuperOrderFilters {
  return {
    ...normalizeClientFilters(filters),
    client_id: filters.client_id,
    store_id: filters.store_id,
    statuses: Array.from(new Set(filters.statuses)).sort(),
  };
}

function filtersEqual<T>(left: T, right: T) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function usePagedFilters<T extends { search: string }>(
  initial: T,
  normalize: (filters: T) => T,
) {
  const [draftFilters, setDraftFilters] = useState<T>(initial);
  const [appliedFilters, setAppliedFilters] = useState<T>(initial);
  const [offset, setOffsetState] = useState(0);
  const [selectionScopeRevision, setSelectionScopeRevision] = useState(0);
  const appliedRef = useRef(appliedFilters);

  useEffect(() => {
    appliedRef.current = appliedFilters;
  }, [appliedFilters]);

  function commit(nextFilters: T) {
    const normalized = normalize(nextFilters);
    if (filtersEqual(appliedRef.current, normalized)) return;
    appliedRef.current = normalized;
    setAppliedFilters(normalized);
    setSelectionScopeRevision((current) => current + 1);
    setOffsetState(0);
  }

  function setOffset(nextOffset: number) {
    setSelectionScopeRevision((current) => current + 1);
    setOffsetState(nextOffset);
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      commit({ ...appliedRef.current, search: draftFilters.search });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [draftFilters.search]);

  return {
    draftFilters,
    setDraftFilters,
    appliedFilters,
    offset,
    setOffset,
    selectionScopeRevision,
    correctOffset: setOffsetState,
    apply: () => commit(draftFilters),
    cancel: () => setDraftFilters(appliedRef.current),
    clear: () => {
      setDraftFilters(initial);
      commit(initial);
    },
  };
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="pedidos-source-filter-field field">
      <span>{label}</span>
      {children}
    </div>
  );
}

function DateFilterInput({
  label,
  value,
  resetVersion,
  onChange,
  onValidityChange,
}: {
  label: string;
  value: string;
  resetVersion: number;
  onChange: (value: string) => void;
  onValidityChange: (isInvalid: boolean) => void;
}) {
  const errorId = useId();
  const [displayValue, setDisplayValue] = useState(
    formatIsoDateForDisplay(value),
  );
  const trimmedDisplayValue = displayValue.trim();
  const isInvalid =
    Boolean(trimmedDisplayValue) && !parseDisplayDateToIso(trimmedDisplayValue);

  useEffect(() => {
    setDisplayValue(formatIsoDateForDisplay(value));
    onValidityChange(false);
  }, [resetVersion, value]);

  return (
    <>
      <input
        type="text"
        inputMode="numeric"
        placeholder="DD/MM/AAAA"
        aria-label={label}
        aria-invalid={isInvalid || undefined}
        aria-describedby={isInvalid ? errorId : undefined}
        value={displayValue}
        onChange={(event) => {
          const nextDisplay = maskOrderDateForDisplay(event.target.value);
          const nextIso = parseDisplayDateToIso(nextDisplay);
          const nextInvalid = Boolean(nextDisplay.trim()) && !nextIso;
          setDisplayValue(nextDisplay);
          onValidityChange(nextInvalid);
          if (!nextInvalid) onChange(nextIso);
        }}
      />
      {isInvalid ? (
        <p id={errorId} role="alert" className="order-filter-field-alert">
          Use o formato DD/MM/AAAA.
        </p>
      ) : null}
    </>
  );
}

export function activeFilterCount(
  filters: Record<string, string | readonly unknown[]>,
) {
  return Object.entries(filters).filter(
    ([key, value]) =>
      key !== "search" &&
      (Array.isArray(value) ? value.length > 0 : Boolean(value)),
  ).length;
}

function getValidPageOffset(total: number, offset: number) {
  if (total <= 0 || offset < total) return offset;
  return Math.floor((total - 1) / PAGE_SIZE) * PAGE_SIZE;
}

function usePageOffsetClamp(
  total: number,
  offset: number,
  setOffset: (offset: number) => void,
) {
  const validOffset = getValidPageOffset(total, offset);
  const isCorrecting = validOffset !== offset;

  useEffect(() => {
    if (isCorrecting) setOffset(validOffset);
  }, [isCorrecting, setOffset, validOffset]);

  return { offset: validOffset, isCorrecting };
}

function Pager({
  total,
  offset,
  onOffsetChange,
}: {
  total: number;
  offset: number;
  onOffsetChange: (offset: number) => void;
}) {
  const pageCount = Math.ceil(total / PAGE_SIZE);
  if (pageCount <= 1) return null;
  const page = Math.floor(offset / PAGE_SIZE);
  return (
    <footer className="pager" data-many-pages={pageCount > 5 || undefined}>
      <span className="pager-range">
        {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} de {total}
      </span>
      <nav className="pager-controls" aria-label="Paginação de pedidos">
        <button
          type="button"
          disabled={page === 0}
          onClick={() => onOffsetChange((page - 1) * PAGE_SIZE)}
        >
          Anterior
        </button>
        {getNumberedPageTokens(page, pageCount).map((token, index) =>
          token === "ellipsis" ? (
            <span
              key={`ellipsis-${index}`}
              className="pager-ellipsis"
              aria-hidden="true"
            >
              …
            </span>
          ) : (
            <button
              key={token}
              type="button"
              aria-label={`Página ${token + 1}`}
              aria-current={token === page ? "page" : undefined}
              className={token === page ? "current" : undefined}
              data-page-role={
                token !== page &&
                (token === 0 || token === pageCount - 1) &&
                Math.abs(token - page) > 1
                  ? "boundary"
                  : undefined
              }
              onClick={() => onOffsetChange(token * PAGE_SIZE)}
            >
              {token + 1}
            </button>
          ),
        )}
        <button
          type="button"
          disabled={page >= pageCount - 1}
          onClick={() => onOffsetChange((page + 1) * PAGE_SIZE)}
        >
          Próximo
        </button>
      </nav>
    </footer>
  );
}

function useSelectedOrder(selectionScope: string) {
  const navigate = useNavigate();
  const routeSearch = useSearch({ from: "/_auth/pedidos" });
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<string>>(
    () => new Set(routeSearch.orderId ? [routeSearch.orderId] : []),
  );
  const selectionScopeRef = useRef(selectionScope);
  const selectionScopeChanged = selectionScopeRef.current !== selectionScope;

  useEffect(() => {
    if (!routeSearch.orderId) return;
    setExpandedOrderIds((current) => {
      if (current.has(routeSearch.orderId!)) return current;
      return new Set(current).add(routeSearch.orderId!);
    });
  }, [routeSearch.orderId]);

  useEffect(() => {
    if (selectionScopeChanged) {
      selectionScopeRef.current = selectionScope;
    }
    if (selectionScopeChanged && expandedOrderIds.size) {
      setExpandedOrderIds(new Set());
      navigate({ to: "/pedidos" as string, search: {} });
    }
  }, [expandedOrderIds.size, navigate, selectionScope, selectionScopeChanged]);

  function toggleOrder(orderId: string) {
    const isOpening = !expandedOrderIds.has(orderId);
    setExpandedOrderIds((current) => toggleOrderExpansion(current, orderId));
    navigate({
      to: "/pedidos" as string,
      search: isOpening ? { orderId } : {},
    });
  }

  function clearSelection() {
    setExpandedOrderIds(new Set());
    navigate({ to: "/pedidos" as string, search: {} });
  }

  return {
    selectedOrderId: selectionScopeChanged ? null : routeSearch.orderId,
    expandedOrderIds: selectionScopeChanged
      ? new Set<string>()
      : expandedOrderIds,
    toggleOrder,
    clearSelection,
  };
}

function summaryFromDetail(detail: EnrichedOrder): OrderSummary {
  const { items, return_summary: _returnSummary, ...ledgerMeta } = detail;
  return {
    ...ledgerMeta,
    total_price: getOrderTotalPrice(detail),
    item_count: items.length,
  };
}

type OrderCardEntryProps = {
  summary: OrderSummary;
  isExpanded: boolean;
  onToggle: () => void;
  isSuper: boolean;
  canManageOrders: boolean;
  isClient: boolean;
  canComplete: boolean;
  onTerminalOrderRemoved: () => void;
  onFeedback: (feedback: OrderFeedback | null) => void;
};

type OrderFeedback = {
  title: string;
  message?: string;
  tone: "success" | "error";
};

function OrderCardEntry({
  summary,
  isExpanded,
  onToggle,
  isSuper,
  canManageOrders,
  isClient,
  canComplete,
  onTerminalOrderRemoved,
  onFeedback,
}: OrderCardEntryProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showChangeRequestModal, setShowChangeRequestModal] = useState(false);
  const [showPendingDetails, setShowPendingDetails] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const pendingTriggerRef = useRef<HTMLButtonElement>(null);
  const pendingDialogRef = useRef<HTMLElement>(null);
  const pendingDialogTitleId = useId();
  const navigate = useNavigate();
  const complete = useCompleteOrder();
  const reopen = useReopenOrder();
  const deleteOrder = useDeleteOrder();
  const approveChangeRequest = useApproveChangeRequest();
  const rejectChangeRequest = useRejectChangeRequest();
  const deleteChangeRequest = useDeleteChangeRequest();
  const detailQuery = useEnrichedOrderDetail(
    isExpanded ||
      showChangeRequestModal ||
      (isClient && summary.status === "change_requested")
      ? summary.id
      : null,
  );
  const detail =
    detailQuery.data && isFinancialEnrichedOrder(detailQuery.data)
      ? detailQuery.data
      : undefined;
  const {
    data: changeRequest,
    isLoading: isChangeRequestLoading,
    isError: isChangeRequestError,
    refetch: refetchChangeRequest,
  } = useChangeRequest(detail?.pending_change_request_id);
  const pendingRequest =
    detail?.status === "change_requested" &&
    changeRequest &&
    isFinancialChangeRequest(changeRequest) &&
    changeRequest.status === "pending" &&
    changeRequest.id === detail.pending_change_request_id &&
    (!summary.pending_change_request_id ||
      changeRequest.id === summary.pending_change_request_id) &&
    changeRequest.order_id === detail.id
      ? changeRequest
      : undefined;
  const canEditPendingRequest = Boolean(pendingRequest);

  function openChangeRequestModal() {
    if (
      (summary.status === "change_requested" ||
        detail?.status === "change_requested") &&
      !canEditPendingRequest
    )
      return;
    setShowChangeRequestModal(true);
  }
  const display = buildOrderCardDisplay({
    order: detail ?? summary,
    today: todaySaoPaulo(),
  });
  const isPending =
    complete.isPending ||
    reopen.isPending ||
    deleteOrder.isPending ||
    approveChangeRequest.isPending ||
    rejectChangeRequest.isPending ||
    deleteChangeRequest.isPending;
  const hasLegacyDirectKg = Boolean(detail?.items.some(isLegacyDirectKgItem));
  const isLegacyReadOnly = hasLegacyDirectKg && !isSuper;
  function closePendingDetails() {
    if (isPending) return;
    setShowPendingDetails(false);
    setRejectMode(false);
    setRejectReason("");
  }
  useEffect(() => {
    if (!showPendingDetails) return;
    const root = document.getElementById("root");
    if (!root) return;
    const previousInert = root.inert;
    root.inert = true;
    return () => {
      root.inert = previousInert;
    };
  }, [showPendingDetails]);
  useDialogFocus(pendingDialogRef, closePendingDetails, {
    enabled: showPendingDetails,
    restoreTarget: pendingTriggerRef.current,
  });
  useEffect(() => {
    if (!isExpanded || detail?.status !== "change_requested") {
      setShowPendingDetails(false);
    }
  }, [isExpanded, detail?.status]);

  useEffect(() => {
    if (!isExpanded && !deleteOrder.isPending) {
      setConfirmDelete(false);
    }
  }, [deleteOrder.isPending, isExpanded]);

  useEffect(() => {
    if (isLegacyReadOnly) {
      setShowAddItem(false);
      setShowChangeRequestModal(false);
      setShowPendingDetails(false);
      setConfirmDelete(false);
      setRejectMode(false);
    }
  }, [isLegacyReadOnly]);

  function clearMutationFeedback() {
    onFeedback(null);
  }

  function reportMutationError(cause: unknown) {
    onFeedback({
      title: getOrderMutationErrorMessage(cause),
      tone: "error",
    });
  }

  async function handleComplete() {
    clearMutationFeedback();
    try {
      await complete.mutateAsync(summary.id);
    } catch (cause) {
      reportMutationError(cause);
    }
  }

  async function handlePrintOrder() {
    if (isExporting) return;
    setIsExporting(true);
    onFeedback(null);
    try {
      const result = await exportOrderPdf(summary.id);
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.filename ?? `pedido-${summary.id}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (cause) {
      onFeedback({
        title: getUserFacingErrorMessage(
          cause,
          "Não foi possível gerar o pedido. Tente novamente.",
        ),
        tone: "error",
      });
    } finally {
      setIsExporting(false);
    }
  }

  async function handleReopen() {
    if (!detail) return;
    clearMutationFeedback();
    try {
      await reopen.mutateAsync(detail.id);
    } catch (cause) {
      reportMutationError(cause);
    }
  }

  async function handleDelete() {
    if ((canManageOrders && !isSuper) || !detail) return;
    clearMutationFeedback();
    try {
      await deleteOrder.mutateAsync(detail.id);
      onTerminalOrderRemoved();
      onFeedback({ title: "Pedido excluído.", tone: "success" });
    } catch (cause) {
      reportMutationError(cause);
    } finally {
      setConfirmDelete(false);
    }
  }

  async function handleApproveChangeRequest() {
    if (!detail?.pending_change_request_id) return;
    clearMutationFeedback();
    try {
      await approveChangeRequest.mutateAsync(detail.pending_change_request_id);
      setShowPendingDetails(false);
    } catch (cause) {
      reportMutationError(cause);
    }
  }

  async function handleRejectChangeRequest() {
    if (
      !isSuper ||
      !detail?.pending_change_request_id ||
      !rejectReason.trim()
    ) {
      return;
    }
    clearMutationFeedback();
    try {
      await rejectChangeRequest.mutateAsync({
        crId: detail.pending_change_request_id,
        payload: { rejection_reason: rejectReason.trim() },
      });
      setRejectMode(false);
      setRejectReason("");
      setShowPendingDetails(false);
    } catch (cause) {
      reportMutationError(cause);
    }
  }

  async function handleDeleteChangeRequest() {
    if (canManageOrders || !pendingRequest) return;
    clearMutationFeedback();
    try {
      await deleteChangeRequest.mutateAsync(pendingRequest.id);
      setShowPendingDetails(false);
    } catch (cause) {
      reportMutationError(cause);
    }
  }

  const hasOrderActions =
    detail && !isLegacyReadOnly
      ? hasOrderCardActions({
          order: detail,
          isSuper,
          canManageOrders,
          isClient,
          canComplete,
          isStale: display.isStale,
          isPastDate: display.isPastDate,
          canOpenCheckin: display.canOpenCheckin,
        })
      : false;
  const hasAddItemPanel = Boolean(
    !isLegacyReadOnly && canManageOrders && showAddItem && detail,
  );
  const hasPendingChangeRequest = Boolean(
    !isLegacyReadOnly &&
    (canManageOrders || isClient) &&
    detail?.status === "change_requested" &&
    detail.pending_change_request_id,
  );
  const displayedChangeRequest =
    canManageOrders && changeRequest && isFinancialChangeRequest(changeRequest)
      ? changeRequest
      : pendingRequest;
  function openPendingEditor() {
    if (!canEditPendingRequest || isPending) return;
    setShowPendingDetails(false);
    window.requestAnimationFrame(openChangeRequestModal);
  }
  const changeRequestActions: ReactNode = canManageOrders ? (
    isSuper && rejectMode ? (
      <>
        <div className="change-request-rejection">
          <label htmlFor={`change-request-rejection-${detail?.id}`}>
            Motivo da recusa
          </label>
          <textarea
            id={`change-request-rejection-${detail?.id}`}
            placeholder="Descreva o motivo"
            value={rejectReason}
            onChange={(event) => setRejectReason(event.target.value)}
            rows={3}
          />
        </div>
        <button
          type="button"
          className="order-action-button danger"
          onClick={handleRejectChangeRequest}
          disabled={isPending || !rejectReason.trim()}
        >
          Confirmar recusa
        </button>
        <button
          type="button"
          className="order-action-button neutral"
          onClick={() => {
            setRejectMode(false);
            setRejectReason("");
          }}
        >
          Cancelar
        </button>
      </>
    ) : (
      <>
        <button
          type="button"
          className="order-action-button primary"
          onClick={handleApproveChangeRequest}
          disabled={isPending}
        >
          <CheckCircle size={14} />
          Aprovar
        </button>
        {isSuper ? (
          <button
            type="button"
            className="order-action-button danger"
            onClick={() => setRejectMode(true)}
            disabled={isPending}
          >
            <X size={14} />
            Recusar
          </button>
        ) : null}
      </>
    )
  ) : (
    <>
      <button
        type="button"
        className="order-action-button neutral"
        onClick={openPendingEditor}
        disabled={isPending || !canEditPendingRequest}
      >
        <Pencil size={14} />
        Editar
      </button>
      <button
        type="button"
        className="order-action-button danger"
        onClick={handleDeleteChangeRequest}
        disabled={isPending || !canEditPendingRequest}
        aria-label="Excluir"
      >
        <Trash2 size={14} />
        Excluir
      </button>
    </>
  );
  const pendingDetails =
    detail && hasPendingChangeRequest ? (
      <ChangeRequestCard
        actionsPlacement="footer"
        title={
          canManageOrders
            ? "Solicitação de alteração pendente"
            : "Sua solicitação de alteração"
        }
        isLoading={isChangeRequestLoading}
        isError={
          isChangeRequestError ||
          (isClient && !isChangeRequestLoading && !canEditPendingRequest)
        }
        onRetry={() => void refetchChangeRequest()}
        footer={
          displayedChangeRequest ? (
            <ChangeRequestDiff
              payload={displayedChangeRequest.changes_payload}
              orderItems={detail.items}
              quotationId={detail.quotation_id ?? undefined}
              storeId={detail.store_id}
              changeRequestId={displayedChangeRequest.id}
            />
          ) : undefined
        }
        actions={
          canManageOrders || canEditPendingRequest
            ? changeRequestActions
            : undefined
        }
      />
    ) : undefined;
  const detailSupplement = hasPendingChangeRequest ? (
    <button
      ref={pendingTriggerRef}
      type="button"
      className="pending-change-trigger"
      aria-haspopup="dialog"
      aria-expanded={showPendingDetails}
      onClick={() => setShowPendingDetails(true)}
      disabled={isPending}
    >
      <CircleAlert size={16} aria-hidden="true" />
      <span>Ver solicitação pendente</span>
    </button>
  ) : undefined;
  const addItemPanel =
    detail && hasAddItemPanel ? (
      <SuperAddItemPanel
        order={detail}
        onClose={() => setShowAddItem(false)}
        onFeedback={onFeedback}
      />
    ) : undefined;
  const actionRail =
    detail && hasOrderActions ? (
      <OrderCardActions
        order={detail}
        isSuper={isSuper}
        canManageOrders={canManageOrders}
        isClient={isClient}
        canComplete={canComplete}
        isStale={display.isStale}
        isPastDate={display.isPastDate}
        canOpenCheckin={display.canOpenCheckin}
        isPending={isPending}
        isExporting={isExporting}
        canEditChangeRequest={
          !isClient ||
          summary.status !== "change_requested" ||
          canEditPendingRequest
        }
        isDeleting={deleteOrder.isPending}
        confirmDelete={confirmDelete}
        showAddItem={showAddItem}
        onComplete={handleComplete}
        onNavigateToQuotation={() =>
          navigate({
            to: "/cotacoes",
            search: { editOrderId: summary.id },
          })
        }
        onOpenChangeRequest={openChangeRequestModal}
        onReopen={handleReopen}
        onToggleAddItem={() => setShowAddItem((previous) => !previous)}
        onOpenCheckin={() =>
          navigate({
            to: "/cargas/$checkinId",
            params: { checkinId: detail.checkin_id! },
          })
        }
        onPrintOrder={isSuper ? handlePrintOrder : undefined}
        onRequestDelete={() => setConfirmDelete(true)}
        onConfirmDelete={handleDelete}
        onCancelDelete={() => setConfirmDelete(false)}
      />
    ) : undefined;
  const summaryHasActions =
    hasOrderCardActions({
      order: summary,
      isSuper,
      canManageOrders,
      isClient,
      canComplete,
      isStale: display.isStale,
      isPastDate: display.isPastDate,
      canOpenCheckin: display.canOpenCheckin,
    }) &&
    hasOrderCardRowActions({
      order: summary,
      isSuper,
      isClient,
      canManageOrders,
      canComplete,
      canOpenCheckin: display.canOpenCheckin,
    });
  const summaryActionRail = summaryHasActions ? (
    <OrderCardActions
      order={summary}
      variant="row"
      isSuper={isSuper}
      canManageOrders={canManageOrders}
      isClient={isClient}
      canComplete={canComplete}
      isStale={display.isStale}
      isPastDate={display.isPastDate}
      canOpenCheckin={display.canOpenCheckin}
      isPending={isPending}
      isExporting={isExporting}
      canEditChangeRequest={
        !isClient ||
        summary.status !== "change_requested" ||
        canEditPendingRequest
      }
      isDeleting={false}
      confirmDelete={false}
      showAddItem={false}
      onComplete={handleComplete}
      onNavigateToQuotation={() =>
        navigate({
          to: "/cotacoes",
          search: { editOrderId: summary.id },
        })
      }
      onOpenChangeRequest={openChangeRequestModal}
      onReopen={() => undefined}
      onToggleAddItem={() => undefined}
      onOpenCheckin={() =>
        navigate({
          to: "/cargas/$checkinId",
          params: { checkinId: summary.checkin_id! },
        })
      }
      onPrintOrder={isSuper ? handlePrintOrder : undefined}
      onRequestDelete={() => undefined}
      onConfirmDelete={() => undefined}
      onCancelDelete={() => undefined}
    />
  ) : undefined;

  return (
    <>
      <OrderCard
        summary={summary}
        detail={detail}
        isExpanded={isExpanded}
        isLoading={detailQuery.isLoading}
        isError={detailQuery.isError}
        onToggle={onToggle}
        onRetry={() => detailQuery.refetch()}
        isSuper={isSuper}
        canManageOrders={canManageOrders}
        showClientName={canManageOrders}
        isStale={display.isStale}
        detailSupplement={detailSupplement}
        addItemPanel={addItemPanel}
        actionRail={isExpanded ? actionRail : summaryActionRail}
        onFeedback={onFeedback}
        onItemRemoved={(response: RemoveOrderItemResponse) => {
          if (response.order_deleted) {
            onTerminalOrderRemoved();
            onFeedback({
              title: response.checkin_deleted
                ? "Pedido e carga excluídos."
                : "Pedido excluído.",
              tone: "success",
            });
            return;
          }
          onFeedback({ title: "Item removido.", tone: "success" });
        }}
      />
      {showPendingDetails &&
        pendingDetails &&
        createPortal(
          <div className="pending-change-layer">
            <button
              type="button"
              className="pending-change-backdrop"
              tabIndex={-1}
              aria-label="Fechar detalhes da solicitação"
              onClick={closePendingDetails}
            />
            <section
              ref={pendingDialogRef}
              className="pending-change-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby={pendingDialogTitleId}
              tabIndex={-1}
            >
              <header className="pending-change-dialog-head">
                <h2 id={pendingDialogTitleId}>
                  {canManageOrders
                    ? "Solicitação de alteração pendente"
                    : "Sua solicitação de alteração"}
                </h2>
                <CloseIconButton
                  label="Fechar detalhes"
                  onClick={closePendingDetails}
                  disabled={isPending}
                />
              </header>
              {pendingDetails}
            </section>
          </div>,
          document.body,
        )}
      {showChangeRequestModal &&
        detail &&
        !isLegacyReadOnly &&
        (summary.status !== "change_requested" || canEditPendingRequest) && (
          <ChangeRequestModal
            order={detail}
            existingCR={pendingRequest}
            onClose={() => setShowChangeRequestModal(false)}
            onFeedback={onFeedback}
          />
        )}
    </>
  );
}

function LedgerList({
  summaries,
  total,
  offset,
  isLoading,
  isError,
  error,
  isRetrying,
  isUpdating,
  isOffsetCorrectionPending,
  onRetryList,
  hasFilters,
  onClearFilters,
  onOffsetChange,
  selectedOrderId,
  expandedOrderIds,
  detail,
  onToggleOrder,
  isSuper,
  canManageOrders,
  isClient,
  canComplete,
  onTerminalOrderRemoved,
  onFeedback,
}: {
  summaries: OrderSummary[];
  total: number;
  offset: number;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  isRetrying: boolean;
  isUpdating: boolean;
  isOffsetCorrectionPending: boolean;
  onRetryList: () => void;
  hasFilters: boolean;
  onClearFilters: () => void;
  onOffsetChange: (offset: number) => void;
  selectedOrderId: string | null;
  expandedOrderIds: Set<string>;
  detail: EnrichedOrder | undefined;
  onToggleOrder: (orderId: string) => void;
  isSuper: boolean;
  canManageOrders: boolean;
  isClient: boolean;
  canComplete: boolean;
  onTerminalOrderRemoved: () => void;
  onFeedback: (feedback: OrderFeedback | null) => void;
}) {
  if (isLoading && !summaries.length) {
    return (
      <div
        className="pedidos-ledger-state"
        role="status"
        aria-label="Carregando pedidos"
      >
        {Array.from({ length: 3 }, (_, index) => (
          <div
            key={index}
            data-testid="pedido-skeleton"
            className="pedido-skeleton-row"
          >
            <span className="pedido-skeleton-line pedido-skeleton-line--wide" />
            <span className="pedido-skeleton-line" />
            <span className="pedido-skeleton-line pedido-skeleton-line--short" />
          </div>
        ))}
      </div>
    );
  }

  const listLoadError = isError ? (
    <InlineLoadError
      title="Pedidos indisponíveis"
      message={getUserFacingErrorMessage(
        error,
        "Não foi possível carregar pedidos.",
      )}
      onRetry={onRetryList}
      isRetrying={isRetrying}
    />
  ) : null;

  if (isError && !summaries.length) return listLoadError;

  if (isOffsetCorrectionPending && !summaries.length) {
    return (
      <div
        className="pedidos-ledger-state"
        role="status"
        aria-label="Atualizando página de pedidos"
      />
    );
  }

  const selectedOutsideCurrentPage =
    selectedOrderId &&
    detail?.id === selectedOrderId &&
    !summaries.some((summary) => summary.id === selectedOrderId)
      ? summaryFromDetail(detail)
      : undefined;
  const displayedSummaries = selectedOutsideCurrentPage
    ? [selectedOutsideCurrentPage, ...summaries]
    : summaries;

  if (!displayedSummaries.length) {
    return (
      <OrderEmptyState
        hasFilters={hasFilters}
        onClearFilters={onClearFilters}
      />
    );
  }

  return (
    <>
      {listLoadError}
      <section
        className="ledger pedidos-source-ledger"
        data-super={isSuper ? "true" : "false"}
        aria-busy={isUpdating || undefined}
      >
        <div
          className={`ledger-head ${isSuper ? "orders-grid" : "client-orders-grid"}`}
          aria-hidden="true"
        >
          <span>{isSuper ? "Pedido" : "Loja"}</span>
          {isSuper && <span>Cliente / loja</span>}
          <span>Data</span>
          <span>Status</span>
          <span>Resumo</span>
          <span>Ações</span>
        </div>
        {displayedSummaries.map((summary) => {
          const isExpanded = expandedOrderIds.has(summary.id);
          return (
            <OrderCardEntry
              key={summary.id}
              summary={summary}
              isExpanded={isExpanded}
              onToggle={() => onToggleOrder(summary.id)}
              isSuper={isSuper}
              canManageOrders={canManageOrders}
              isClient={isClient}
              canComplete={canComplete}
              onTerminalOrderRemoved={onTerminalOrderRemoved}
              onFeedback={onFeedback}
            />
          );
        })}
        <Pager total={total} offset={offset} onOffsetChange={onOffsetChange} />
      </section>
    </>
  );
}

function ClientOrdersView({
  isClient,
  canComplete,
}: {
  isClient: boolean;
  canComplete: boolean;
}) {
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState<OrderFeedback | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dateInvalidity, setDateInvalidity] = useState({
    exact: false,
    from: false,
    to: false,
  });
  const [dateResetVersion, setDateResetVersion] = useState(0);
  const filters = usePagedFilters(
    DEFAULT_CLIENT_FILTERS,
    normalizeClientFilters,
  );
  const queryFilters = toClientQueryFilters(filters.appliedFilters);
  const summariesQuery = useOrderSummaries({
    limit: PAGE_SIZE,
    offset: filters.offset,
    filters: queryFilters,
  });
  const summaryPage = summariesQuery.data;
  const summaries =
    summaryPage && !isOperationalOrderSummaryPage(summaryPage)
      ? summaryPage.items
      : [];
  usePrefetchEnrichedOrderDetails(
    summaries.slice(0, DETAIL_PREFETCH_COUNT).map((summary) => summary.id),
  );
  const selection = useSelectedOrder(
    JSON.stringify({
      filters: queryFilters,
      revision: filters.selectionScopeRevision,
    }),
  );
  const detailQuery = useEnrichedOrderDetail(selection.selectedOrderId);
  const activeCount = activeFilterCount(filters.appliedFilters);
  const total = summaryPage
    ? isOperationalOrderSummaryPage(summaryPage)
      ? summaryPage.count
      : summaryPage.total
    : 0;
  const detail =
    detailQuery.data && isFinancialEnrichedOrder(detailQuery.data)
      ? detailQuery.data
      : undefined;
  const pageOffset = usePageOffsetClamp(
    total,
    filters.offset,
    filters.correctOffset,
  );

  return (
    <>
      <OperationalPageFrame
        size="standard"
        className="pedidos-page app-page-frame"
      >
        <OperationalPageHeader
          title="Meus pedidos"
          actions={
            <button
              type="button"
              className="pedidos-new-item"
              onClick={() => navigate({ to: "/cotacoes" as string })}
            >
              <Plus size={15} />
              Novo item
            </button>
          }
        />
        <OperationalToolbar
          className="pedidos-toolbar"
          search={
            <OperationalSearchField
              value={filters.draftFilters.search}
              placeholder="Buscar loja"
              ariaLabel="Buscar loja"
              onChange={(search) =>
                filters.setDraftFilters((previous) => ({
                  ...previous,
                  search,
                }))
              }
            />
          }
          filters={
            <OperationalFilterSurface
              open={filtersOpen}
              onOpenChange={setFiltersOpen}
              title="Filtros"
              activeCount={activeCount}
              onApply={filters.apply}
              applyDisabled={Object.values(dateInvalidity).some(Boolean)}
              onClear={() => {
                setDateResetVersion((current) => current + 1);
                filters.clear();
              }}
              onCancel={() => {
                setDateInvalidity({ exact: false, from: false, to: false });
                filters.cancel();
              }}
            >
              <FilterField label="Status">
                <select
                  aria-label="Status"
                  value={filters.draftFilters.status}
                  onChange={(event) =>
                    filters.setDraftFilters((previous) => ({
                      ...previous,
                      status: event.target.value as OrderStatus | "",
                    }))
                  }
                >
                  <option value="">Todos</option>
                  {ORDER_STATUS_FILTER_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </FilterField>
              <FilterField label="Data">
                <DateFilterInput
                  label="Data"
                  value={
                    filters.draftFilters.date_from ===
                    filters.draftFilters.date_to
                      ? filters.draftFilters.date_from
                      : ""
                  }
                  resetVersion={dateResetVersion}
                  onChange={(date) =>
                    filters.setDraftFilters((previous) => ({
                      ...previous,
                      date_from: date,
                      date_to: date,
                    }))
                  }
                  onValidityChange={(exact) =>
                    setDateInvalidity((previous) => ({ ...previous, exact }))
                  }
                />
              </FilterField>
              <details
                className="pedidos-date-range"
                onToggle={() => {
                  setDateInvalidity({ exact: false, from: false, to: false });
                  setDateResetVersion((current) => current + 1);
                }}
              >
                <summary>
                  Intervalo de datas
                  {filters.draftFilters.date_from &&
                  filters.draftFilters.date_to &&
                  filters.draftFilters.date_from !==
                    filters.draftFilters.date_to
                    ? ` · ${formatIsoDateForDisplay(filters.draftFilters.date_from)} – ${formatIsoDateForDisplay(filters.draftFilters.date_to)}`
                    : ""}
                </summary>
                <FilterField label="Data da cotação (início)">
                  <DateFilterInput
                    label="Data da cotação (início)"
                    value={filters.draftFilters.date_from}
                    resetVersion={dateResetVersion}
                    onChange={(date_from) =>
                      filters.setDraftFilters((previous) => ({
                        ...previous,
                        date_from,
                      }))
                    }
                    onValidityChange={(from) =>
                      setDateInvalidity((previous) => ({ ...previous, from }))
                    }
                  />
                </FilterField>
                <FilterField label="Data da cotação (fim)">
                  <DateFilterInput
                    label="Data da cotação (fim)"
                    value={filters.draftFilters.date_to}
                    resetVersion={dateResetVersion}
                    onChange={(date_to) =>
                      filters.setDraftFilters((previous) => ({
                        ...previous,
                        date_to,
                      }))
                    }
                    onValidityChange={(to) =>
                      setDateInvalidity((previous) => ({ ...previous, to }))
                    }
                  />
                </FilterField>
              </details>
            </OperationalFilterSurface>
          }
          status={
            summariesQuery.isFetching && summaries.length ? (
              <span role="status" aria-label="Atualizando pedidos">
                Atualizando…
              </span>
            ) : undefined
          }
        />
        <LedgerList
          summaries={summaries}
          total={total}
          offset={pageOffset.offset}
          isLoading={summariesQuery.isLoading}
          isError={summariesQuery.isError}
          error={summariesQuery.error}
          isRetrying={summariesQuery.isFetching}
          isUpdating={summariesQuery.isFetching && summaries.length > 0}
          isOffsetCorrectionPending={pageOffset.isCorrecting}
          onRetryList={() => summariesQuery.refetch()}
          hasFilters={activeCount > 0 || Boolean(filters.appliedFilters.search)}
          onClearFilters={filters.clear}
          onOffsetChange={filters.setOffset}
          selectedOrderId={selection.selectedOrderId}
          expandedOrderIds={selection.expandedOrderIds}
          detail={detail}
          onToggleOrder={selection.toggleOrder}
          isSuper={false}
          canManageOrders={false}
          isClient={isClient}
          canComplete={canComplete}
          onTerminalOrderRemoved={selection.clearSelection}
          onFeedback={setFeedback}
        />
        {feedback ? (
          <FloatingToast
            title={feedback.title}
            message={feedback.message}
            tone={feedback.tone}
            onDismiss={() => setFeedback(null)}
          />
        ) : null}
      </OperationalPageFrame>
    </>
  );
}

function SuperOrdersView({
  canComplete,
  isSuper,
  canManageOrders,
}: {
  canComplete: boolean;
  isSuper: boolean;
  canManageOrders: boolean;
}) {
  const [feedback, setFeedback] = useState<OrderFeedback | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dateInvalidity, setDateInvalidity] = useState({
    from: false,
    to: false,
  });
  const [dateResetVersion, setDateResetVersion] = useState(0);
  const filters = usePagedFilters(DEFAULT_SUPER_FILTERS, normalizeSuperFilters);
  const queryFilters = toSuperQueryFilters(filters.appliedFilters);
  const summariesQuery = useOrderSummaries({
    limit: PAGE_SIZE,
    offset: filters.offset,
    filters: queryFilters,
  });
  const { data: clientsData } = useClients();
  const { data: storesData } = useStores();
  const summaryPage = summariesQuery.data;
  const summaries =
    summaryPage && !isOperationalOrderSummaryPage(summaryPage)
      ? summaryPage.items
      : [];
  usePrefetchEnrichedOrderDetails(
    summaries.slice(0, DETAIL_PREFETCH_COUNT).map((summary) => summary.id),
  );
  const selection = useSelectedOrder(
    JSON.stringify({
      filters: queryFilters,
      revision: filters.selectionScopeRevision,
    }),
  );
  const detailQuery = useEnrichedOrderDetail(selection.selectedOrderId);
  const activeCount = activeFilterCount(filters.appliedFilters);
  const total = summaryPage
    ? isOperationalOrderSummaryPage(summaryPage)
      ? summaryPage.count
      : summaryPage.total
    : 0;
  const detail =
    detailQuery.data && isFinancialEnrichedOrder(detailQuery.data)
      ? detailQuery.data
      : undefined;
  const pageOffset = usePageOffsetClamp(
    total,
    filters.offset,
    filters.correctOffset,
  );

  return (
    <>
      <OperationalPageFrame
        size="standard"
        className="pedidos-page app-page-frame"
      >
        <OperationalPageHeader title="Pedidos" />
        <OperationalToolbar
          className="pedidos-toolbar"
          search={
            <OperationalSearchField
              value={filters.draftFilters.search}
              placeholder="Buscar cliente ou loja"
              ariaLabel="Buscar cliente ou loja"
              onChange={(search) =>
                filters.setDraftFilters((previous) => ({
                  ...previous,
                  search,
                }))
              }
            />
          }
          filters={
            <OperationalFilterSurface
              open={filtersOpen}
              onOpenChange={setFiltersOpen}
              title="Filtros"
              activeCount={activeCount}
              onApply={filters.apply}
              applyDisabled={dateInvalidity.from || dateInvalidity.to}
              onClear={() => {
                setDateResetVersion((current) => current + 1);
                filters.clear();
              }}
              onCancel={() => {
                setDateInvalidity({ from: false, to: false });
                filters.cancel();
              }}
            >
              <FilterField label="Cliente">
                <OperationalSelect
                  ariaLabel="Cliente"
                  value={filters.draftFilters.client_id}
                  onChange={(client_id) =>
                    filters.setDraftFilters((previous) => ({
                      ...previous,
                      client_id,
                    }))
                  }
                  menuLayer="overlay"
                  options={[
                    { value: "", label: "Todos clientes" },
                    ...(clientsData?.items ?? []).map((client) => ({
                      value: client.id,
                      label: client.name,
                    })),
                  ]}
                />
              </FilterField>
              <FilterField label="Loja">
                <OperationalSelect
                  ariaLabel="Loja"
                  value={filters.draftFilters.store_id}
                  onChange={(store_id) =>
                    filters.setDraftFilters((previous) => ({
                      ...previous,
                      store_id,
                    }))
                  }
                  menuLayer="overlay"
                  options={[
                    { value: "", label: "Todas lojas" },
                    ...(storesData?.items ?? []).map((store) => ({
                      value: store.id,
                      label: store.name,
                    })),
                  ]}
                />
              </FilterField>
              <FilterField label="Status">
                <OrderStatusCheckboxGroup
                  value={filters.draftFilters.statuses}
                  onChange={(statuses) =>
                    filters.setDraftFilters((previous) => ({
                      ...previous,
                      statuses,
                    }))
                  }
                />
              </FilterField>
              <FilterField label="Data da cotação (início)">
                <DateFilterInput
                  label="Data da cotação (início)"
                  value={filters.draftFilters.date_from}
                  resetVersion={dateResetVersion}
                  onChange={(date_from) =>
                    filters.setDraftFilters((previous) => ({
                      ...previous,
                      date_from,
                    }))
                  }
                  onValidityChange={(from) =>
                    setDateInvalidity((previous) => ({ ...previous, from }))
                  }
                />
              </FilterField>
              <FilterField label="Data da cotação (fim)">
                <DateFilterInput
                  label="Data da cotação (fim)"
                  value={filters.draftFilters.date_to}
                  resetVersion={dateResetVersion}
                  onChange={(date_to) =>
                    filters.setDraftFilters((previous) => ({
                      ...previous,
                      date_to,
                    }))
                  }
                  onValidityChange={(to) =>
                    setDateInvalidity((previous) => ({ ...previous, to }))
                  }
                />
              </FilterField>
            </OperationalFilterSurface>
          }
          status={
            summariesQuery.isFetching && summaries.length ? (
              <span role="status" aria-label="Atualizando pedidos">
                Atualizando…
              </span>
            ) : undefined
          }
        />
        <LedgerList
          summaries={summaries}
          total={total}
          offset={pageOffset.offset}
          isLoading={summariesQuery.isLoading}
          isError={summariesQuery.isError}
          error={summariesQuery.error}
          isRetrying={summariesQuery.isFetching}
          isUpdating={summariesQuery.isFetching && summaries.length > 0}
          isOffsetCorrectionPending={pageOffset.isCorrecting}
          onRetryList={() => summariesQuery.refetch()}
          hasFilters={activeCount > 0 || Boolean(filters.appliedFilters.search)}
          onClearFilters={filters.clear}
          onOffsetChange={filters.setOffset}
          selectedOrderId={selection.selectedOrderId}
          expandedOrderIds={selection.expandedOrderIds}
          detail={detail}
          onToggleOrder={selection.toggleOrder}
          isSuper={isSuper}
          canManageOrders={canManageOrders}
          isClient={false}
          canComplete
          onTerminalOrderRemoved={selection.clearSelection}
          onFeedback={setFeedback}
        />
        {feedback ? (
          <FloatingToast
            title={feedback.title}
            message={feedback.message}
            tone={feedback.tone}
            onDismiss={() => setFeedback(null)}
          />
        ) : null}
      </OperationalPageFrame>
    </>
  );
}

export function PedidosPage() {
  const { data: currentUser } = useCurrentUser();
  if (!currentUser) return null;
  const isSuper = currentUser.role === "super";
  const isAdminHelper = currentUser.role === "admin_helper";
  const { canManageOrders } = getOperationalCapabilities(currentUser);
  const canComplete = canCompleteClientOrder(currentUser) || canManageOrders;
  return isAdminHelper ? (
    <AdminHelperOperationalOrdersView />
  ) : canManageOrders ? (
    <SuperOrdersView
      canComplete={canComplete}
      isSuper={isSuper}
      canManageOrders={canManageOrders}
    />
  ) : (
    <ClientOrdersView
      isClient={isClientLike(currentUser)}
      canComplete={canComplete}
    />
  );
}
